// The quick go-to (OEM-213): Ctrl+/ (Cmd+/ on a Mac), or the search icon in the toolbar, opens a window
// that finds an item on the board this tab is on by its number (188, 03-188, #188, OEM-188) or by words
// (title, description, notes, tags, project name), and goes there: its card scrolled to and flashed
// when the board on screen shows it, else a panel beside the board (Shift+Enter: a panel always).
// The ranking is quickFind.ts (pure, tested); this is the window. A combobox over a listbox, the
// highlighted row named by aria-activedescendant; the focus stays in the box (a trap) and goes back
// where it was on Esc. On a phone it is a sheet from the top.

import { assigneesOf, flowLane, kindFlow, type BoardItem } from "@boem/core/shared";
import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode, type Ref } from "react";
import { createPortal } from "react-dom";
import { projectTone } from "../grouping.ts";
import { goToKey, hitNumber, quickFind, shortcutLabel, type FindContext, type FindHit, type Snippet } from "../quickFind.ts";
import { Avatar } from "../util.tsx";
import { Icon } from "./Icons.tsx";
import { KindBadge } from "./Structure.tsx";

/** Whether this is a Mac (the shortcut reads ⌘/ there). */
export const isMac = () => typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

/** Opens the quick go-to (App provides it; the toolbar's search icon uses it). */
export const GoToContext = createContext<{ open: () => void; mac: boolean } | null>(null);

/** The toolbar's search icon: opens the quick go-to, for touch and for finding out it exists. */
export function GoToButton({ onOpen, mac }: { onOpen: () => void; mac: boolean }) {
  const tip = `Go to an item (${shortcutLabel(mac)})`;
  return (
    <button type="button" className="chip qg-btn tip" aria-label={tip} data-tip={tip} aria-haspopup="dialog" onClick={onOpen}>
      <Icon name="search" />
    </button>
  );
}

/** The search icon where a toolbar has one (the board, the archive, What's new): nothing outside App's provider. */
export function GoToToolbarButton() {
  const goTo = useContext(GoToContext);
  return goTo ? <GoToButton onOpen={goTo.open} mac={goTo.mac} /> : null;
}

/** Where an item is: its queue (or To do / Done for a task, Open / Closed for a project). */
function laneOfHit(it: BoardItem): { text: string; cls: string } {
  const flow = kindFlow(it.kind);
  const closed = it.status === "closed";
  if (flow === "none") return { text: closed ? "Closed" : "Open", cls: `s-${it.status} l-${closed ? "closed" : "todo"}` };
  if (flow === "done") return { text: closed ? "Done" : "To do", cls: `s-${it.status} l-${closed ? "closed" : "todo"}` };
  const lane = flowLane(it);
  const text = { todo: "To do", review: "PR review", deploy: "Deploy", verify: "Verify", closed: it.verified ? "Verified" : "Closed" }[lane];
  return { text, cls: `s-${it.status} l-${lane}` };
}

const FIELD: Record<Snippet["field"], string> = { description: "Description", note: "Note", tags: "Tags", project: "Project" };

/** A snippet with its matched words in <mark>. */
function SnippetLine({ s }: { s: Snippet }) {
  const parts: ReactNode[] = [];
  let at = 0;
  s.marks.forEach(([a, b], i) => {
    if (a > at) parts.push(s.text.slice(at, a));
    parts.push(<mark key={i}>{s.text.slice(a, b)}</mark>);
    at = b;
  });
  parts.push(s.text.slice(at));
  return (
    <span className="qg-snip">
      <span className="qg-field">{FIELD[s.field]}</span> {parts}
    </span>
  );
}

const optId = (num: number) => `qg-opt-${num}`;

export interface QuickGoToViewProps {
  query: string;
  hits: FindHit[];
  /** the highlighted row (-1: none) */
  active: number;
  /** the board it searches: "OEMUAP", "Gautam's board", "03 · Delivery app" first */
  scope: string;
  mac: boolean;
  onQuery: (q: string) => void;
  onActive: (i: number) => void;
  onGo: (i: number, panel: boolean) => void;
  onClose: () => void;
  inputRef?: Ref<HTMLInputElement>;
  dialogRef?: Ref<HTMLDivElement>;
}

/** The window itself (no state of its own: QuickGoTo keeps it), so it renders anywhere, tests included. */
export function QuickGoToView({ query, hits, active, scope, mac, onQuery, onActive, onGo, onClose, inputRef, dialogRef }: QuickGoToViewProps) {
  const cur = hits[active];
  const onKey = (e: KeyboardEvent) => {
    const a = goToKey({ key: e.key, shiftKey: e.shiftKey, isComposing: e.nativeEvent.isComposing }, active, hits.length);
    if (!a) return;
    e.preventDefault();
    e.stopPropagation();
    if ("move" in a) onActive(a.move);
    else if ("go" in a) onGo(a.go, a.panel);
    else onClose();
  };
  // a focus trap: Tab goes between the box and Close, never out to the board behind
  const trap = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab") return;
    const xs = [...e.currentTarget.querySelectorAll<HTMLElement>("input, button")];
    if (!xs.length) return;
    const i = xs.indexOf(document.activeElement as HTMLElement);
    const n = (i + (e.shiftKey ? -1 : 1) + xs.length) % xs.length;
    xs[n]!.focus();
    e.preventDefault();
  };
  const empty = !query.trim();
  return (
    <div className="qg-scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="qg" role="dialog" aria-modal="true" aria-label={`Go to an item on ${scope}`} ref={dialogRef} onKeyDown={trap}>
        <div className="qg-bar">
          <Icon name="search" />
          <input
            ref={inputRef}
            className="qg-input"
            type="text"
            role="combobox"
            aria-expanded={hits.length > 0}
            aria-controls="qg-list"
            aria-autocomplete="list"
            aria-activedescendant={cur ? optId(cur.item.num) : undefined}
            aria-label={`Go to an item on ${scope}: a number (188, 03-188, #188, OEM-188) or words`}
            placeholder="188, 03-07, OEM-188 or words…"
            autoComplete="off"
            spellCheck={false}
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            onKeyDown={onKey}
          />
          <button type="button" className="qg-close" onClick={onClose} aria-label="Close">
            Esc
          </button>
        </div>
        <div className="qg-scope">
          {empty ? "Recently updated on " : "On "}
          <b>{scope}</b>
          {empty ? "" : ` · ${hits.length ? `${hits.length}${hits.length >= 20 ? "+" : ""} match${hits.length === 1 ? "" : "es"}` : "no matches"}`}
        </div>
        <ul className="qg-list" id="qg-list" role="listbox" aria-label="Matches">
          {hits.map((x, i) => {
            const it = x.item;
            const lane = laneOfHit(it);
            const proj = it.project;
            return (
              <li
                key={it.num}
                id={optId(it.num)}
                role="option"
                aria-selected={i === active}
                className="qg-row"
                data-archived={it.archived ? "1" : undefined}
                onMouseMove={() => i !== active && onActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => onGo(i, e.shiftKey)}
              >
                <span className="qg-no">{hitNumber(x)}</span>
                <span className="qg-main">
                  <span className="qg-line">
                    <KindBadge kind={it.kind} />
                    <span className="qg-title">{it.title}</span>
                  </span>
                  {x.snippet && <SnippetLine s={x.snippet} />}
                </span>
                <span className="qg-meta">
                  {proj && (
                    <span className="pchip qg-pchip" data-tone={projectTone(proj)} title={`Project ${proj.title}`}>
                      <b>{proj.projectNo != null ? String(proj.projectNo).padStart(2, "0") : `#${proj.num}`}</b>
                      <span className="pchip-t">{proj.title}</span>
                    </span>
                  )}
                  {assigneesOf(it).slice(0, 3).map((p) => (
                    <Avatar key={p.id} name={p.name} />
                  ))}
                  <span className={`pill ${lane.cls}`}>{lane.text}</span>
                  {it.archived && <span className="role archived-badge">archived</span>}
                </span>
              </li>
            );
          })}
        </ul>
        <div className="qg-foot" aria-hidden="true">
          <span>
            <kbd>&uarr;</kbd>
            <kbd>&darr;</kbd> move
          </span>
          <span>
            <kbd>Enter</kbd> go
          </span>
          <span>
            <kbd>Shift+Enter</kbd> in a panel
          </span>
          <span>
            <kbd>Esc</kbd> close
          </span>
          <span className="qg-sc">{shortcutLabel(mac)}</span>
        </div>
        <div className="qg-sr" aria-live="polite">
          {empty ? "" : hits.length ? `${hits.length} matches. ${cur ? `${hitNumber(cur)} ${cur.item.title}` : ""}` : "No matches"}
        </div>
      </div>
    </div>
  );
}

/**
 * The window, with its state: the query, the highlighted row, the focus. `items`: this board's
 * (and its archive's); `ctx`: the project in focus; `onGo`: go to the item (`panel`: Shift).
 */
export function QuickGoTo({
  items,
  ctx,
  scope,
  onGo,
  onClose,
}: {
  items: BoardItem[];
  ctx: FindContext;
  scope: string;
  onGo: (item: BoardItem, panel: boolean) => void;
  onClose: (restoreFocus: boolean) => void;
}) {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const mac = useMemo(isMac, []);
  const hits = useMemo(() => quickFind(items, query, ctx), [items, query, ctx]);
  // a new query highlights the first row again
  useEffect(() => setActive(hits.length ? 0 : -1), [query, hits.length]);
  useEffect(() => input.current?.focus({ preventScroll: true }), []);
  // the highlighted row stays in view
  useLayoutEffect(() => {
    const it = hits[active]?.item;
    if (it) document.getElementById(optId(it.num))?.scrollIntoView({ block: "nearest" });
  }, [active, hits]);
  if (typeof document === "undefined") return null;
  return createPortal(
    <QuickGoToView
      query={query}
      hits={hits}
      active={active}
      scope={scope}
      mac={mac}
      onQuery={setQuery}
      onActive={setActive}
      onGo={(i, panel) => {
        const it = hits[i]?.item;
        if (it) onGo(it, panel);
      }}
      onClose={() => onClose(true)}
      inputRef={input}
    />,
    document.body,
  );
}
